import { useCallback, useEffect, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { mapHttpErrorToMessage } from "../../../utils/mapHttpErrorToMessage";
import { submitDashboardFeedback } from "../api/dashboard.api";
import type { FeedbackFormState, FeedbackResponse, FeedbackScreenshot } from "../types/dashboard.types";
import { safeErrorText } from "../utils/dashboardStatus.utils";

const MAX_SCREENSHOT_SIZE = 5 * 1024 * 1024;

const initialForm: FeedbackFormState = {
  title: "",
  body: "",
  screenshots: [],
};

const readScreenshot = (file: File): Promise<FeedbackScreenshot> =>
  new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("Only image files are allowed."));
      return;
    }
    if (file.size > MAX_SCREENSHOT_SIZE) {
      reject(new Error("Screenshot is too large."));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      if (!result) reject(new Error("Unable to read screenshot."));
      else resolve({ base64: result, name: file.name, size: file.size, type: file.type });
    };
    reader.onerror = () => reject(new Error("Unable to read screenshot."));
    reader.readAsDataURL(file);
  });

export const useDashboardFeedback = (requesterName: string, t: TFunction) => {
  const mountedRef = useRef(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FeedbackFormState>(initialForm);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setLoading(false);
  }, []);

  const updateField = useCallback((field: "title" | "body", value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  }, []);

  const addScreenshots = useCallback(
    async (files: FileList | null) => {
      setError(null);
      const selected = Array.from(files ?? []);
      if (!selected.length) return;
      try {
        const screenshots = await Promise.all(selected.map(readScreenshot));
        if (mountedRef.current) setForm((current) => ({ ...current, screenshots: [...current.screenshots, ...screenshots] }));
      } catch (readError) {
        if (mountedRef.current) setError(safeErrorText(readError, t("dashboard.report_screenshot_invalid", { defaultValue: "Select image files up to 5 MB." })));
      }
    },
    [t],
  );

  const submit = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const response: FeedbackResponse = await submitDashboardFeedback(form, requesterName);
      if (!mountedRef.current) return;
      const id = response.number ?? response.id ?? response.issueNumber ?? response.issue_id;
      setMessage(
        id
          ? t("dashboard.report_sent_with_id", {
              defaultValue: "Report created (#{{id}}).",
              id,
            })
          : t("dashboard.report_sent", { defaultValue: "Report created." }),
      );
      setForm(initialForm);
    } catch (submitError) {
      const mapped = mapHttpErrorToMessage(submitError, t) as { message?: string } | string | null;
      if (mountedRef.current) {
        setError(
          (typeof mapped === "string" ? mapped : mapped?.message) ||
            safeErrorText(submitError, t("dashboard.report_failed", { defaultValue: "Sending failed." })),
        );
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [form, loading, requesterName, t]);

  return {
    open,
    setOpen,
    close,
    form,
    updateField,
    addScreenshots,
    loading,
    message,
    error,
    submit,
    canSubmit: Boolean(form.title.trim() || form.body.trim() || form.screenshots.length),
  };
};
